/* Klubregister: Lesen und Schreiben der Tabelle football_clubs und ihrer Funktionen.
 *
 * Zugriffsregeln der Datenbank (supabase/klubregister-1-tabelle.sql, -3-funktionen.sql):
 *   lesen                          alle fünf Rollen
 *   anlegen, ändern, löschen       nur admin
 *   Zusammenführen, Nutzungszahlen nur admin (Funktionen prüfen es selbst)
 * Ein benutzter Verein lässt sich nicht löschen (Fremdschlüssel, Fehler 23503).
 *
 * NICHT zu verwechseln mit domain/clubs.js (Club-Wechsler der App, "Grassrunners FC" als Mandant).
 *
 * Fehler werden hier zentral behandelt: Details in die Konsole, nach oben nur ein allgemeiner Schlüssel
 * (errors.<schlüssel> in den Sprachdateien) — nie die Meldung der Datenbank. Es werden nie Dateien gelöscht.
 */

import { supabase } from './client.js';
import { rowToClub } from '../domain/footballClub.js';

function fail(where, error, key) {
  console.error(`[footballClubs] ${where} fehlgeschlagen:`, error && error.message);
  throw new Error(key);
}

/** Alle Vereine, alphabetisch (die Sortierung ohne Akzente macht domain/footballClub.js sortClubs). */
export async function listClubs() {
  if (!supabase) throw new Error('notConnected');
  const { data, error } = await supabase.from('football_clubs').select('*').order('name', { ascending: true });
  if (error) fail('Laden', error, 'loadFailed');
  return (data || []).map(rowToClub);
}

/** Neuen Verein anlegen. `id` darf mitgegeben werden (das Logo liegt dann schon im Ordner dieses Vereins). */
export async function createClub({ id, name, logoPath = null }) {
  if (!supabase) throw new Error('notConnected');
  const row = { name, logo_path: logoPath || null };
  if (id) row.id = id;
  const { data, error } = await supabase.from('football_clubs').insert(row).select().single();
  if (error) fail('Anlegen', error, error.code === '23505' ? 'clubExists' : 'saveFailed');
  return rowToClub(data);
}

/** Name und/oder Logo ändern. patch = { name?, logoPath? }. Liefert den gespeicherten Stand. */
export async function updateClub(id, patch) {
  if (!supabase) throw new Error('notConnected');
  const row = {};
  if ('name' in patch) row.name = patch.name;
  if ('logoPath' in patch) row.logo_path = patch.logoPath || null;
  const { data, error } = await supabase.from('football_clubs').update(row).eq('id', id).select();
  /* Ohne Recht meldet die Datenbank keinen Fehler, sondern "0 Zeilen" — das darf nicht als Erfolg durchgehen. */
  if (error || !data || data.length !== 1) {
    fail('Ändern', error || { message: 'keine Zeile geändert' }, error && error.code === '23505' ? 'clubExists' : 'saveFailed');
  }
  return rowToClub(data[0]);
}

/** Verein löschen — nur, wenn nichts auf ihn verweist (sonst 'clubInUse'). Die Logo-Datei bleibt liegen. */
export async function deleteClub(id) {
  if (!supabase) throw new Error('notConnected');
  const { data, error } = await supabase.from('football_clubs').delete().eq('id', id).select('id');
  if (error) fail('Löschen', error, error.code === '23503' ? 'clubInUse' : 'saveFailed');
  if (!data || data.length !== 1) fail('Löschen', { message: 'keine Zeile gelöscht' }, 'saveFailed');
}

/** Benutzung je Verein (nur admin): Map id -> { players, transfers }. */
export async function clubUsage() {
  if (!supabase) throw new Error('notConnected');
  const { data, error } = await supabase.rpc('football_club_usage');
  if (error) fail('Nutzungszahlen', error, 'loadFailed');
  const map = new Map();
  for (const r of data || []) map.set(r.club_id, { players: Number(r.players) || 0, transfers: Number(r.transfers) || 0 });
  return map;
}

/** Zusammenführen (nur admin). Liefert den Logo-Pfad des gelöschten Vereins; die Datei bleibt liegen. */
export async function mergeClubs(sourceId, targetId) {
  if (!supabase) throw new Error('notConnected');
  const { data, error } = await supabase.rpc('merge_football_clubs', { p_source: sourceId, p_target: targetId });
  if (error) fail('Zusammenführen', error, error.code === '42501' ? 'notAllowed' : 'saveFailed');
  return data || null;
}

/**
 * Welche dieser Pfade nennt das Register als Logo? Schutz beim Löschen von Dateien (fileCleanup,
 * playerDeletion): Was hier steht, wird nie gelöscht. Wirft bei Fehler — der Aufrufer löscht dann NICHTS.
 */
export async function registerLogoRefs(paths) {
  if (!supabase) throw new Error('notConnected');
  const list = (paths || []).filter(Boolean);
  if (!list.length) return [];
  const { data, error } = await supabase.from('football_clubs').select('logo_path').in('logo_path', list);
  if (error) fail('Prüfung der Logo-Pfade', error, 'loadFailed');
  return (data || []).map((r) => r.logo_path).filter(Boolean);
}

/** Alle Logo-Pfade des Registers (für das Aufräumen nach dem Speichern). Wirft bei Fehler. */
export async function allRegisterLogoPaths() {
  if (!supabase) throw new Error('notConnected');
  const { data, error } = await supabase.from('football_clubs').select('logo_path').not('logo_path', 'is', null);
  if (error) fail('Prüfung der Logo-Pfade', error, 'loadFailed');
  return (data || []).map((r) => r.logo_path).filter(Boolean);
}

